import { Icons } from '@/components/icons';
import { Button } from '@/components/primitives/Button';
import {
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from '@/components/primitives/Popover';
import {
  SheetContent,
  SheetHeader,
  SheetRoot,
  SheetTrigger,
} from '@/components/primitives/Sheet';
import { Typography } from '@/components/primitives/Typography';
import { manageFlowBookingSelector } from '@/features/manage/manageSelectors';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import useDownloadTicket from '@/hooks/useDownloadTicket';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import { getBookingFulfillments } from '@/utils/booking';
import { breakpoints } from '@/utils/breakpoints';
import type { FC } from 'react';
import { useMediaQuery } from 'react-responsive';

const TicketsDownload: FC = () => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const profile = useSelector(profileDataSelector);
  const booking = useSelector(manageFlowBookingSelector);

  const data = {
    bookingId: booking?.id,
    code: booking?.code,
    payerEmail: profile?.email,
    fulfillments: getBookingFulfillments(booking),
  };

  const {
    isDownloading,
    isPkPassDownloading,
    handleTicketDownload,
    handlePkPassDownload,
  } = useDownloadTicket(data);

  function renderDownloadButton() {
    return (
      <Button
        data-testid="profile-booking-download"
        variant="secondary"
        size={isLaptopOrBigger ? 'regular' : 'large'}
        fullWidth
      >
        <Icons.download width={16} height={16} />
        <TransText i18nKey="download" />
        <Icons.chevronDown height={16} width={16} />
      </Button>
    );
  }

  function renderDownloadContent() {
    return (
      <div className="flex flex-col gap-2 p-3 laptop:p-0">
        <Button
          data-testid="profile-download-tickets"
          variant="secondary"
          size={isLaptopOrBigger ? 'regular' : 'large'}
          fullWidth
          disabled={isDownloading}
          onClick={handleTicketDownload}
        >
          {isDownloading ? (
            <Icons.loader height={16} width={16} className="animate-spin" />
          ) : (
            <Icons.download height={16} width={16} />
          )}
          <TransText i18nKey="downloadTickets" />
        </Button>
        <Button
          data-testid="profile-add-to-wallet"
          variant="secondary"
          size={isLaptopOrBigger ? 'regular' : 'large'}
          fullWidth
          disabled={isPkPassDownloading}
          onClick={handlePkPassDownload}
        >
          {isPkPassDownloading ? (
            <Icons.loader height={16} width={16} className="animate-spin" />
          ) : (
            <Icons.pkPassWallet height={16} width={16} />
          )}
          <TransText i18nKey="addToWallet" />
        </Button>
      </div>
    );
  }

  if (isLaptopOrBigger) {
    return (
      <PopoverRoot>
        <PopoverTrigger asChild>{renderDownloadButton()}</PopoverTrigger>
        <PopoverContent className="flex flex-col gap-2 p-4">
          {renderDownloadContent()}
        </PopoverContent>
      </PopoverRoot>
    );
  }

  return (
    <SheetRoot>
      <SheetTrigger asChild>{renderDownloadButton()}</SheetTrigger>
      <SheetContent side="bottom">
        <SheetHeader showClose>
          <div className="flex items-center gap-3">
            <Icons.download width={16} height={16} className="text-primary" />
            <Typography variant="subtitle">
              <TransText i18nKey="download" />
            </Typography>
          </div>
        </SheetHeader>
        {renderDownloadContent()}
      </SheetContent>
    </SheetRoot>
  );
};

export default TicketsDownload;
